import {
  isPresaleActive,
  PRESALE_CAMPAIGN,
  PRESALE_PURCHASE_INTENT_FLOW,
  type PresaleCampaign,
} from "./presale-campaign";

/**
 * The place shape the resume decision reads — one item of `GET /members/places`, declared
 * here rather than imported from the generated client so this stays a dependency-free
 * calculation (depend on the shape, not the type), exactly as `PresaleRoutePlace` does.
 */
export type PresaleResumePlace = {
  /**
   * The place's latest `Membership` → `Subscription` → `Plan`, or null when it has none.
   * Non-null means this place has been PAID for, whatever its intent says.
   */
  plan?: unknown;
  /** The place's latest `PurchaseIntent` — `/members/places` embeds at most one per place. */
  purchaseIntent?: {
    id: string;
    status?: string | null;
    stripeInvoiceId?: string | null;
    payload?: Record<string, unknown> | null;
    createdAt: string;
  } | null;
};

/** Everything `/presale/checkout` needs in its search to reopen an unfinished reservation. */
export type PresaleResume = {
  purchaseIntentId: string;
  /**
   * Required, not optional: the checkout step reopens a cart, and an intent carrying no
   * checkout link has none — so it is not resumable at all rather than
   * resumable-without-a-cart.
   */
  checkoutLinkId: string;
  discountCode?: string;
  referId?: string;
  referredBy?: string;
};

/** A payload string, treating the JSONB's `null` and `""` as absent. */
function payloadString(
  payload: Record<string, unknown> | null | undefined,
  key: string,
): string | undefined {
  const value = payload?.[key];
  return typeof value === "string" && value !== "" ? value : undefined;
}

/**
 * Unfinished presale reservations, newest intent first.
 *
 * `payload.flow` is what makes an intent a presale one — the funnel stamps it in
 * `usePresalePlace().reserve` right after `add-my-place`, because both funnels create their
 * intent through that one endpoint. It replaced two inferences: the campaign zipcode (a
 * place is only ever stamped after passing that gate at reserve time, and several campaign
 * zips are already-served market) and a second request confirming `checkoutLinkId` against
 * the campaign's `CheckoutLink` rows. The list is final — the caller resumes the first item.
 *
 * "Unfinished" is three guards, not a status allow-list. `POST /checkout/pay` writes
 * `SUCCESS` after the charge and `FAILED` only when `stripe.subscriptions.create` throws
 * — so an intent whose subscription was created but whose Membership write then failed is
 * still `PENDING` WITH a live Stripe subscription. `stripeInvoiceId` (success path only)
 * and `plan` (written with the Membership) catch that; the status alone would not, and
 * resuming it would charge the buyer twice.
 *
 * A genuinely `FAILED` intent — a declined card — stays resumable, which is the point:
 * `POST /checkout/pay` does not check status, so retrying it works.
 */
export function findPresaleResumes(
  places: readonly PresaleResumePlace[] | undefined,
  now: Date,
  campaign: PresaleCampaign = PRESALE_CAMPAIGN,
): PresaleResume[] {
  // On launch day the market opens and buyers go through `/my/onboarding` instead, so
  // the whole feature switches itself off here — no deploy, same as the other gates.
  if (!isPresaleActive(now, campaign)) return [];

  return (
    (places ?? [])
      .filter((place) => {
        if (place.plan != null) return false;
        const intent = place.purchaseIntent;
        if (!intent) return false;
        if (
          payloadString(intent.payload, "flow") !== PRESALE_PURCHASE_INTENT_FLOW
        ) {
          return false;
        }
        if (intent.status === "SUCCESS") return false;
        if (intent.stripeInvoiceId) return false;
        return Boolean(payloadString(intent.payload, "checkoutLinkId"));
      })
      .map((place) => {
        // The filter above proved both of these; narrow without asserting.
        const intent = place.purchaseIntent;
        const checkoutLinkId = payloadString(intent?.payload, "checkoutLinkId");
        if (!intent || !checkoutLinkId) return null;
        return {
          purchaseIntentId: intent.id,
          checkoutLinkId,
          discountCode: payloadString(intent.payload, "discountCode"),
          referId: payloadString(intent.payload, "referId"),
          referredBy: payloadString(intent.payload, "referredBy"),
          createdAt: intent.createdAt,
        };
      })
      .filter((candidate): candidate is NonNullable<typeof candidate> =>
        Boolean(candidate),
      )
      // Newest reservation wins — the same rule the platform applies wherever intents are
      // ranked. `/members/places` orders places by `address1`, so without this the winner would
      // be address-order. ISO-8601 sorts lexicographically; the id tie-break stays
      // chronological because the repo's nanoids are time-sortable.
      .sort(
        (a, b) =>
          b.createdAt.localeCompare(a.createdAt) ||
          b.purchaseIntentId.localeCompare(a.purchaseIntentId),
      )
      .map(({ createdAt: _createdAt, ...resume }) => resume)
  );
}
